import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  useGetEnginesId,
  getGetEnginesIdQueryKey,
} from '@/types/origin-point-engine'
import { mapEngineDetailToEditorState } from '@/features/editor/lib/editor.mappers'
import { useEditorStore } from '@/features/editor/stores/editor-store'

export function useEditorBootstrap(
  engineId: string | null | undefined,
  vType?: 'draft' | 'reviewing' | 'published'
) {
  const init = useEditorStore((s) => s.init)
  const hydrate = useEditorStore((s) => s.hydrate)

  // 1) engineId 变化：先重置编辑器状态，但不建立已保存基线
  useEffect(() => {
    init(engineId ?? null, { markSaved: false })
  }, [engineId, init])

  // 2) 拉取详情（orval 已在 queryOptions 里做了 enabled: !!id）
  const query = useGetEnginesId(
    engineId ?? '',
    { vType }, // 传递 vType 参数给 API
    {
      query: {
        // 这里可以覆写一些你想要的行为
        enabled: Boolean(engineId),
        refetchOnWindowFocus: false,
        retry: 1,
        // staleTime: 30_000, // 可选：30s 内不重复拉
      },
    }
  )

  // 3) 数据到达：mapper -> hydrate，并 markSaved 建立 baseline（初始不 dirty）
  useEffect(() => {
    if (!engineId) return
    if (!query.data) return

    const mapped = mapEngineDetailToEditorState(query.data)

    hydrate(
      {
        draft: mapped.draft,
        knowledge: mapped.knowledge,
        publishStatus: mapped.publishStatus,
        reviewMeta: mapped.reviewMeta,
      },
      { markSaved: true }
    )
  }, [engineId, query.data, hydrate])

  const { isLoading, isError, error } = query
  return { isLoading, isError, error }
}

/**
 * 手动使引擎数据缓存失效
 *
 */
export function useInvalidateEngineCache() {
  const qc = useQueryClient()

  return {
    invalidate: async (engineId: string) => {
      await qc.invalidateQueries({
        queryKey: getGetEnginesIdQueryKey(engineId),
      })
    },
  }
}
